import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useProviderSearch } from '@/hooks/useProviderSearch';
import { ProviderCard } from '@/components/ProviderCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin, Filter, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const getProviderTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    'veterinarian': 'veterinarians',
    'veterinarian_individual': 'veterinarians',
    'veterinary_clinic': 'veterinary clinics',
    'groomer': 'groomers',
    'grooming_spa': 'grooming services',
    'pet_sitter': 'pet sitters',
    'boarding': 'boarding facilities',
    'dog_walker': 'dog walkers',
    'pet_store': 'pet stores',
    'trainer': 'pet trainers',
  };
  return labels[type] || 'service providers';
};

export default function ServicesSearch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { detectLocation, isDetecting } = useGeolocation();

  const [location, setLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [providerType, setProviderType] = useState(searchParams.get('type') || '');
  const [emergencyOnly, setEmergencyOnly] = useState(searchParams.get('emergency') === 'true');
  const [openNow, setOpenNow] = useState(false);
  const [maxDistance, setMaxDistance] = useState(50);

  const { data: providers, isLoading, refetch } = useProviderSearch({
    user_lat: location?.lat || 0,
    user_lon: location?.lon || 0,
    provider_type: providerType,
    emergency_only: emergencyOnly,
    max_distance_km: maxDistance,
    open_now: openNow,
  });

  useEffect(() => {
    loadUserLocation();
  }, []);

  const loadUserLocation = async () => {
    try {
      // First, try to get user's saved profile location
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('latitude, longitude, city, state')
          .eq('user_id', user.id)
          .single();

        if (profile?.latitude && profile?.longitude) {
          console.log('Using profile location:', profile);
          setLocation({ 
            lat: profile.latitude, 
            lon: profile.longitude 
          });
          toast.success(`Searching near ${profile.city}, ${profile.state}`);
          return; // Found profile location, don't use GPS
        }
      }
    } catch (error) {
      console.error('Error loading profile location:', error);
    }

    // Fallback: Use GPS detection
    await handleDetectLocation();
  };

  const handleDetectLocation = async () => {
    const result = await detectLocation();
    if (result) {
      setLocation({ lat: result.latitude, lon: result.longitude });
      toast.success(t('location.detected'));
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="bg-card border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/services')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-foreground">
              {emergencyOnly ? t('services.emergency') : t('services.searchResults')}
            </h1>
            <Button
              variant="outline"
              onClick={handleDetectLocation}
              disabled={isDetecting}
            >
              <MapPin className="w-4 h-4 mr-2" />
              {isDetecting ? t('location.detecting') : t('location.update')}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-card p-4 rounded-lg shadow-sm sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-foreground" />
                <h3 className="font-semibold text-foreground">{t('services.filters')}</h3>
              </div>

              <div className="flex items-center justify-between mb-4">
                <Label htmlFor="open-now">{t('services.openNow')}</Label>
                <Switch
                  id="open-now"
                  checked={openNow}
                  onCheckedChange={setOpenNow}
                />
              </div>

              <div className="mb-4">
                <Label htmlFor="max-distance">
                  {t('services.maxDistance')}: {maxDistance} km
                </Label>
                <Input
                  id="max-distance"
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={maxDistance}
                  onChange={(e) => setMaxDistance(Number(e.target.value))}
                  className="mt-2"
                />
              </div>

              <Button
                className="w-full"
                onClick={() => refetch()}
              >
                {t('services.applyFilters')}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-3">
            {isLoading || isDetecting ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !location ? (
              <div className="text-center py-12 bg-card rounded-lg p-8">
                <MapPin className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-foreground">
                  {t('services.locationRequired')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {t('services.locationRequiredDesc')}
                </p>
                <Button onClick={handleDetectLocation}>
                  {t('location.detect')}
                </Button>
              </div>
            ) : providers && providers.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('services.resultsFound', { count: providers.length })}
                </p>
                {providers.map((provider) => (
                  <ProviderCard key={provider.id} provider={provider} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card rounded-lg p-8">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    {t('services.noResults')}
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    {emergencyOnly 
                      ? `No emergency services found within ${maxDistance}km of your location.`
                      : `No ${getProviderTypeLabel(providerType)} found within ${maxDistance}km of your location.`}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Are you a service provider? <Link to="/register-provider" className="text-primary hover:underline">Register your business</Link> to appear in search results.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <Button 
                      variant="outline"
                      onClick={() => setMaxDistance(Math.min(maxDistance + 25, 200))}
                    >
                      Search up to {Math.min(maxDistance + 25, 200)}km
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setOpenNow(false);
                        setEmergencyOnly(false);
                        setProviderType('');
                        refetch();
                      }}
                    >
                      Show All Services
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
